import React, { Dispatch, SetStateAction, useMemo } from 'react';
import Image from 'next/image';
import ProductData from '@customTypes/product';
import addButton from '@assets/images/add.svg';
import addButtonAlt from '@assets/images/add-alt.svg';
import actionTypes from '@context/actionTypes';
import { ExperimentTypeABC, UserAction } from '@customTypes/context';
import {
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME_2,
} from '@constants/amplitudeExperiments';

type Props = {
  counter: number;
  date: Date | undefined;
  dispatch: Dispatch<UserAction>;
  personType: string;
  productData: ProductData;
  setParentCounter: Dispatch<SetStateAction<number>>;
  validate: (value: number, personType: string) => boolean;
  isDisabled?: boolean;
  onChange?: (value: number) => void;
  onIncreaseCounterClick?: () => void;
  ctasUpdateExperiment?: ExperimentTypeABC;
};

const defaultProps = {
  isDisabled: false,
  onChange: () => {},
  onIncreaseCounterClick: () => {},
};

export default function CounterVariant({
  counter,
  date,
  dispatch,
  isDisabled,
  onChange,
  onIncreaseCounterClick,
  personType,
  productData,
  setParentCounter,
  validate,
  ctasUpdateExperiment,
}: Props) {
  const checkValidity = (value: number) => {
    if (date) {
      if (validate(value, personType)) {
        setParentCounter(counter + 1);
        if (onChange) onChange(counter + 1);
      }
    } else {
      // Open date picker when there's no date selected and the user clicked on the "+" icon.
      dispatch({
        type: actionTypes.OPEN_DATE_PICKER,
        payload: true,
      });

      // Set the SmartCalendar filter to current product.
      dispatch({
        type: actionTypes.SET_SMART_CALENDAR_PRODUCT_FILTER,
        payload: {
          label: productData.name,
          value: productData.product_time_group_id || productData.product_id.toString(),
        },
      });
    }
  };

  const decreaseCounter = () => {
    const newCounter = counter > 0 ? counter - 1 : 0;
    if (setParentCounter) setParentCounter(newCounter);
    if (onChange) onChange(newCounter);
  };

  const increaseCounter = () => {
    if (onIncreaseCounterClick) {
      onIncreaseCounterClick();
    }

    checkValidity(counter);
  };

  const buttonBgColor = useMemo(() => {
    if (ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME) {
      return 'bg-solar-button-primary-default hover:bg-solar-button-primary-hover active:bg-solar-button-primary-pressed';
    }

    if (ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME_2) {
      return 'bg-solar-button-ultramarine-default hover:bg-solar-button-ultramarine-hover active:bg-solar-button-ultramarine-pressed';
    }

    return 'bg-rp-primary active:bg-rp-primary-dark';
  }, [ctasUpdateExperiment]);

  return (
    <div className="flex w-32 h-10 border border-solid rounded-full items-center px-1 justify-between font-rp-pn-regular bg-white shadow-lg">
      <button
        type="button"
        className="rounded-full h-8 w-8 border border-solid flex justify-center items-center text-3xl cursor-pointer active:bg-rp-light-gray"
        onClick={decreaseCounter}
        disabled={isDisabled}
        aria-label={`Decrease ${personType}`}
      >
        <div className="w-3 h-0.5 bg-rp-primary-black" />
      </button>

      <div
        className={`w-12 h-full border-l border-r border-solid flex justify-center items-center ${
          isDisabled ? 'text-rp-danger-2' : 'text-base'
        }`}
      >
        {counter}
      </div>

      <button
        type="button"
        className={`rounded-full h-8 w-8 border border-solid flex justify-center items-center text-3xl cursor-pointer ${
          isDisabled ? 'bg-white' : buttonBgColor
        }`}
        onClick={increaseCounter}
        disabled={isDisabled}
        aria-label={`Increase ${personType}`}
      >
        <Image src={isDisabled ? addButtonAlt : addButton} alt="add-button" />
      </button>
    </div>
  );
}

CounterVariant.defaultProps = defaultProps;
